import React, { useEffect, useRef, useState } from "react";
import { fetchBalanceEntries, fetchBalance } from "../services/dataService";
import { deleteEntry } from "../services/deleteEntry";
import TransactionItem from "../components/reusable/TransactionItem";
import LoadingScreen from "./reusable/loadingScreen";
import { useTranslation } from "react-i18next";
import {
  calculateLedgerBalance,
  getTransactionContribution,
} from "../utils/balanceCalculation";

const formatSignedAmount = (amount) =>
  `${amount >= 0 ? "+" : "−"} ₹ ${Math.abs(amount).toFixed(2)}`;

function BalanceSheet({ startDate, endDate, userId, role }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);
  const [Total, setTotal] = useState(0);
  const [previousBalance, setPreviousBalance] = useState(0);
  const requestIdRef = useRef(0);
  const [balanceData, setBalanceData] = useState({
    morning: {},
    evening: {},
    borrow: [],
  });

  useEffect(() => {
    fetchData();
    return () => {
      requestIdRef.current += 1;
    };
  }, [startDate, endDate, userId]); 

  const handleDelete = async (itemId, time) => {
    try {
      await deleteEntry(itemId, time);
      await fetchData();
    } catch (error) {
      console.error("Error deleting entry:", error);
    }
  };

  const fetchData = async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    try {
      const [balanceSummary, sheetData] = await Promise.all([
        fetchBalance(startDate, endDate, userId),
        fetchBalanceEntries(startDate, endDate, userId),
      ]);

      if (requestId !== requestIdRef.current) return;

      const { feed, ghee, moneyGiven, moneyReceived, Before } = balanceSummary;
      const ledger = calculateLedgerBalance({
        openingBalance: Before?.total,
        milk:
          Number(sheetData.morning?.total || 0) +
          Number(sheetData.evening?.total || 0),
        feed: feed?.totalMoney,
        ghee: ghee?.totalMoney,
        moneyReceived: moneyReceived?.totalMoney,
        moneyGiven: moneyGiven?.totalMoney,
      });

      setBalanceData(sheetData);
      setPreviousBalance(ledger.openingBalance);
      setTotal(ledger.activity);
      setGrandTotal(ledger.closingBalance);
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error("Error fetching data:", error);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  // --- Helper to translate dynamic backend items ---
  const getTranslatedLabel = (originalItemName) => {
    const normalizedItem = String(originalItemName || "").trim().toLowerCase();

    if (normalizedItem === "feed") return t('balanceSheet.items.feed');
    if (normalizedItem === "ghee") return t('balanceSheet.items.ghee');

    const isMoneyRec = ["money received", "money recieved", "receive money"].includes(normalizedItem);
    const isMoneyGiven = ["money given", "give money"].includes(normalizedItem);

    if (isMoneyRec) return t('balanceSheet.items.money_received');
    if (isMoneyGiven) return t('balanceSheet.items.money_given');

    return originalItemName; 
  };

  const getItemKind = (originalItemName) => {
    const normalizedItem = String(originalItemName || "").trim().toLowerCase();
    if (normalizedItem === "feed") return "feed";
    if (normalizedItem === "ghee") return "ghee";
    if (
      [
        "money received",
        "money recieved",
        "receive money",
        "money given",
        "give money",
      ].includes(normalizedItem)
    ) {
      return "money";
    }
    return "item";
  };

  const tableData = [
    {
      item: t('balanceSheet.items.milk_m'),
      date: "",
      quantityDisplay: t("adminBalance.weight_detail", {
        weight: Number(balanceData.morning.weight || 0).toFixed(1),
      }),
      name: "",
      money: balanceData.morning.total || 0,
      kind: "milk",
    },
    {
      item: t('balanceSheet.items.milk_e'),
      date: "",
      quantityDisplay: t("adminBalance.weight_detail", {
        weight: Number(balanceData.evening.weight || 0).toFixed(1),
      }),
      name: "",
      money: balanceData.evening.total || 0,
      kind: "milk",
    },
    ...balanceData.borrow.map((item) => {
      let displayItem = getTranslatedLabel(item.item);
    
      return {
        item: displayItem,
        date: item.date,
        quantityDisplay:
          item.quantity && item.price
            ? t("adminBalance.quantity_detail", {
                quantity: item.quantity,
                price: Number(item.price).toFixed(2),
              })
            : "",
        name:
          String(item.name || "").trim().toLowerCase() === "dairy"
            ? t("adminBalance.dairy")
            : item.name,
        money: getTransactionContribution(item.item, item.money),
        id: item.id,
        kind: getItemKind(item.item),
      };
    }),    
  ];

  return (
    <div className="bg-white border border-line rounded-2xl shadow-sm p-4 md:p-6 mb-10">
      
      {/* Header */}
      <h3 className="text-[16px] font-bold text-primary mb-4 pb-3 border-b border-line">
        {t('balanceSheet.title')}
      </h3>

      {/* Transaction Items */}
      <div className="space-y-4">
        <TransactionItem
          data={tableData}
          onDelete={handleDelete}
          role={role}
        />
      </div>

      {/* Summary Section */}
      <div className="mt-8 px-5 py-5 bg-paper/50 rounded-2xl shadow-sm border border-line">
        
        {/* Net Difference */}
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-line">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white border border-line rounded-full shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-[10.5px] font-bold uppercase tracking-widest text-[#6B7280]">
              {t('balanceSheet.summary.net_diff')}
            </span>
          </div>
          <span className={`font-mono font-bold text-[16px] ${Total >= 0 ? "text-credit" : "text-debit"}`}>
            {formatSignedAmount(Total)}
          </span>
        </div>

        {/* Previous Balance */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white border border-line rounded-full shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-[10.5px] font-bold uppercase tracking-widest text-[#6B7280]">
              {t('balanceSheet.summary.prev_bal')}
            </span>
          </div>
          <span
            className={`font-mono font-bold text-[15px] px-3 py-1 rounded-lg ${
              previousBalance >= 0
                ? "bg-credit/10 text-credit"
                : "bg-debit/10 text-debit"
            }`}
          >
            {formatSignedAmount(previousBalance)}
          </span>
        </div>

        {/* Final Total */}
        <div className="flex justify-between items-center mt-6 pt-5 border-t border-line">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-[12px] font-bold uppercase tracking-widest text-primary">
              {t('balanceSheet.summary.final_total')}
            </span>
          </div>
          <span
            className={`font-mono font-bold text-[22px] px-4 py-2 rounded-xl shadow-sm border whitespace-nowrap ${
              grandTotal >= 0
                ? "bg-credit/10 text-credit border-credit/20"
                : "bg-debit/10 text-debit border-debit/20"
            }`}
          >
            {formatSignedAmount(grandTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default BalanceSheet;
